/**
 * 浏览器机翻（Chrome 翻译等）会把 React 管理的文本节点替换成 <font> 包裹的新节点，
 * React 后续更新/卸载时按旧引用调用 removeChild/insertBefore，节点已不在原父节点下，
 * 抛出 NotFoundError 导致整页崩溃（见 facebook/react#11538）。
 *
 * 这里在父子关系不成立（即即将崩溃）时刷新页面，让 React 重新渲染出完整的 DOM。
 * 为防止翻译自动重开后同一操作反复触发、陷入刷新循环，短时间内只刷新一次；
 * 超出后回落到原生行为（照常抛错）。必须在 React 首次渲染前调用。
 */

const RELOAD_KEY = 'dom-translation-crash-reload-at'
/** 刷新后待提示标记，挂载后由 consumeTranslationReloadNotice 读取并清除 */
const NOTICE_KEY = 'dom-translation-crash-reload-notice'
/** 两次自动刷新的最小间隔 */
const RELOAD_COOLDOWN_MS = 10_000

let reloading = false

/** 本次页面加载是否由翻译崩溃自动刷新而来；读后即清除，只提示一次 */
export function consumeTranslationReloadNotice(): boolean {
  try {
    if (!sessionStorage.getItem(NOTICE_KEY)) return false
    sessionStorage.removeItem(NOTICE_KEY)
    return true
  } catch {
    return false
  }
}

/** 返回 true 表示已发起刷新，调用方应跳过本次 DOM 操作 */
function reloadOnce(): boolean {
  if (reloading) return true
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY))
    if (last && Date.now() - last < RELOAD_COOLDOWN_MS) return false
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
    sessionStorage.setItem(NOTICE_KEY, '1')
  } catch {
    // sessionStorage 不可用时无法防循环，不自动刷新
    return false
  }
  reloading = true
  window.location.reload()
  return true
}

export function patchDomForTranslation() {
  if (typeof Node !== 'function' || !Node.prototype) return

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    // 刷新已发起：跳过本次操作，避免 React 在页面卸载前抛错
    if (child.parentNode !== this && reloadOnce()) return child
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this && reloadOnce()) return newNode
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
